/**
 * Plano atual (spec 011, secao 2): com mais de um plano na mesma conta, a area trabalha sempre sobre um deles. A
 * escolha fica na URL (`?plano=`), pra link copiado e botao voltar funcionarem, e o aparelho lembra a ultima. Quem tem
 * um plano so nunca ve escolha. O conteudo da pessoa (Academy, certificados, perfil, pagamentos) fica fora disto.
 */
import { CartaoDoPlanoAtual, formatarDia, type OpcaoDePlano, type PlanoNaCasca } from "@mtc/ui";
import { createContext, useContext, useEffect, useMemo, useRef, type ReactNode } from "react";
import { useSearchParams } from "react-router";
import { marca } from "./config";
import { contaDaGlobal } from "./corretora";
import { useMatriculas } from "./paginas/Planos";
import { suspensaoPelaCobranca } from "./passo";
import { ETAPA_CURTA, STATUS_DA_MATRICULA, type Matricula } from "./tipos";

const CHAVE = "mtc:plano-atual";
type Contexto = { planos: Matricula[]; atual: Matricula | null; escolher: (id: string | null) => void; verTodos: boolean; carregando: boolean; erro: boolean; recarregar: () => void };
const Ctx = createContext<Contexto>({ planos: [], atual: null, escolher: () => undefined, verTodos: true, carregando: false, erro: false, recarregar: () => undefined });

/** Plano encerrado nao pede mais nada: fica em "Planos anteriores", so pra consultar. */
const encerrado = (m: Matricula) => m.status === "ENCERRADA";

/** O que pesa mais quando a pessoa entra sem ter escolhido: bloqueio, pagamento, pedido, depois o resto. */
function urgencia(m: Matricula): number {
  if (encerrado(m)) return 5;
  if (m.bloqueio || suspensaoPelaCobranca(m)) return 0;
  if (m.modulos.mensalidade?.situacao === "VENCIDA" || m.modulos.mensalidade?.situacao === "VENCE_HOJE") return 1;
  if (m.modulos.aprovacao?.pedido?.status === "AGUARDANDO_PAGAMENTO") return 1;
  if (m.proximo_passo) return 2;
  return m.status === "ATIVA" ? 3 : 4;
}

/**
 * Em qual plano a area abre. A escolha na URL sempre vale (link copiado, consultar um plano antigo pelo menu). O plano
 * lembrado pelo aparelho vale so se ainda esta em andamento, ou se nao ha outro em andamento: um cliente teve o plano
 * encerrado, comprou outro igual, e a area seguia abrindo o encerrado que o aparelho lembrava ("Plano encerrado" no
 * lugar do plano ativo; caso real de 04/11). Sem escolha, o mais urgente.
 */
export function planoInicial(planos: Matricula[], pedido: string | null, lembrado: string | null): Matricula | null {
  const naUrl = pedido ? planos.find((m) => m.id === pedido) : undefined;
  if (naUrl) return naUrl;
  const doAparelho = lembrado ? planos.find((m) => m.id === lembrado) : undefined;
  if (doAparelho && (!encerrado(doAparelho) || planos.every(encerrado))) return doAparelho;
  return planos.length ? [...planos].sort((a, b) => urgencia(a) - urgencia(b))[0]! : null;
}

export function ProvedorDePlano({ children }: { children: ReactNode }) {
  const q = useMatriculas();
  const [busca, setBusca] = useSearchParams();
  const pedido = busca.get("plano");
  const planos = useMemo(() => q.data?.itens ?? [], [q.data]);
  const lembrado = useRef<string | null>(null);
  if (lembrado.current === null) {
    try {
      lembrado.current = localStorage.getItem(CHAVE) ?? "";
    } catch {
      lembrado.current = "";
    }
  }
  const atual = planoInicial(planos, pedido, lembrado.current || null);
  useEffect(() => {
    if (!atual) return;
    try {
      localStorage.setItem(CHAVE, atual.id);
    } catch {
      /* sem armazenamento: so nao lembra */
    }
  }, [atual?.id]);
  const escolher = (id: string | null) => {
    const p = new URLSearchParams(busca);
    if (id) p.set("plano", id);
    else p.delete("plano");
    setBusca(p, { replace: true });
  };
  const valor: Contexto = { planos, atual, escolher, verTodos: pedido === "todos", carregando: q.isLoading, erro: q.isError, recarregar: () => void q.refetch() };
  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>;
}

export const usePlanoAtual = () => useContext(Ctx);

/** Passos do backend que pedem algo do cliente: o cartao do plano ganha o selo "Pendente". */
const PASSOS_COM_ACAO = new Set(["AGENDAR_INICIO", "REAGENDAR"]);

/**
 * A conta que a casca mostra, pelo que o resumo trouxe (nunca pela marca): a subconta (resumo do nelogica-v1, Prop) ou a
 * conta na corretora (resumo do nelogica-v2, Global, onde a conta e um numero so).
 */
function contaDaCasca(m: Matricula): Pick<PlanoNaCasca, "subconta" | "rotuloDaConta"> {
  const c = m.modulos.corretora;
  if (c?.subconta) return { subconta: c.subconta };
  const g = contaDaGlobal(m);
  return g?.conta ? { subconta: g.conta, rotuloDaConta: "Conta" } : {};
}

/** A etapa por extenso: a da familia (resumo `etapas`) ou a registrada na corretora (Challenge ou Funded, spec 044). */
const etapaPorExtenso = (m: Matricula): string | null => m.modulos.etapas?.etapa_rotulo ?? contaDaGlobal(m)?.etapa_rotulo ?? null;

/** Plataforma e data da compra no seletor: dois "Black Intermediario" so se distinguem por isso (caso real de 04/11). */
function detalhe(m: Matricula): string | null {
  const partes = [m.modulos.corretora?.plataforma_rotulo, m.comprada_em ? `comprado em ${formatarDia(m.comprada_em)}` : null].filter(Boolean);
  return partes.length ? partes.join(" · ") : null;
}

/** A linha de apoio de cada plano no seletor: o que pesa mais pra ele agora. */
function apoio(m: Matricula): string {
  if (encerrado(m)) return "Encerrado: histórico, certificados e pagamentos continuam aqui";
  if (m.bloqueio) return "Bloqueado: resolva pra voltar a operar";
  const suspensao = suspensaoPelaCobranca(m);
  if (suspensao) return `Suspenso: pague até ${formatarDia(suspensao.ate)}`;
  if (m.proximo_passo) return m.proximo_passo.titulo;
  const pedido = linhaDaAprovacao(m);
  if (pedido) return pedido;
  return m.modulos.etapas ? ETAPA_CURTA[m.modulos.etapas.etapa] : (contaDaGlobal(m)?.etapa_rotulo ?? STATUS_DA_MATRICULA[m.status].rotulo);
}

/** O pedido de aprovacao do Challenge em uma linha (spec 064), enquanto ele pede algo ou espera a conta Funded. */
function linhaDaAprovacao(m: Matricula): string | null {
  const p = m.modulos.aprovacao?.pedido;
  if (!p || contaDaGlobal(m)?.etapa === "FUNDED") return null;
  if (p.status === "PENDENTE") return "Aprovação em análise: operações pausadas";
  if (p.status === "AGUARDANDO_PAGAMENTO" && !p.pago) return "Aprovado: falta a taxa de ativação";
  if (p.status === "APROVADA" && p.aguardando_liberacao) return `Aprovado: conta Funded em até ${p.prazo ?? "o prazo combinado"}`;
  return null;
}

/** A matricula traduzida pro cartao da casca. So traduz o que veio do backend; nenhuma regra nova aqui. */
function paraCasca(m: Matricula): PlanoNaCasca {
  const e = m.modulos.etapas;
  const lista = e?.caminho.map((x) => x.etapa);
  const etapas = e && lista?.length ? { lista: lista.map((x) => ETAPA_CURTA[x]), atual: Math.max(0, lista.indexOf(e.etapa)) } : undefined;
  const plataforma = m.modulos.corretora?.plataforma_rotulo ?? null;
  if (encerrado(m)) return { nome: m.plano.nome, apoio: "Plano encerrado, só pra consulta", plataforma, etapas, situacao: "encerrado", selo: { rotulo: "Encerrado", tom: "neutro" } };
  if (m.bloqueio) {
    return {
      nome: m.plano.nome,
      apoio: etapaPorExtenso(m) ?? STATUS_DA_MATRICULA[m.status].rotulo,
      plataforma,
      etapas,
      ...contaDaCasca(m),
      passo: m.bloqueio.mensagem,
      situacao: "bloqueado",
      selo: { rotulo: "Bloqueado", tom: "erro" },
      acao: { rotulo: m.bloqueio.acao?.rotulo ?? "Pagar mensalidade", href: m.bloqueio.acao?.link ?? "/mensalidades" },
    };
  }
  // Suspenso pela taxa de manutencao (spec 064): como o bloqueio, com o prazo e o caminho pra pagar.
  const suspensao = suspensaoPelaCobranca(m);
  if (suspensao) {
    return {
      nome: m.plano.nome,
      apoio: etapaPorExtenso(m) ?? STATUS_DA_MATRICULA[m.status].rotulo,
      plataforma,
      etapas,
      ...contaDaCasca(m),
      passo: suspensao.encerrado ? `O prazo pra pagar acabou em ${formatarDia(suspensao.ate)}` : `Pague até ${formatarDia(suspensao.ate)} pra voltar a operar`,
      situacao: "bloqueado",
      selo: { rotulo: "Suspenso", tom: "erro" },
      acao: { rotulo: suspensao.encerrado ? `Ver a ${suspensao.nome}` : `Pagar a ${suspensao.nome}`, href: "/mensalidades" },
    };
  }
  const pedido = linhaDaAprovacao(m);
  return {
    nome: m.plano.nome,
    apoio: etapaPorExtenso(m) ?? STATUS_DA_MATRICULA[m.status].rotulo,
    plataforma,
    etapas,
    ...contaDaCasca(m),
    // Avanco aprovado esperando a conta nova (spec 038, 1.5): a promessa em uma linha, no lugar do "peca a analise".
    passo: m.proximo_passo?.titulo ?? (e?.avanco_aguardando_liberacao ? `Avanço aprovado: conta nova em até ${e.avanco_aguardando_liberacao.prazo ?? "o prazo combinado"}` : pedido),
    selo: (m.proximo_passo && PASSOS_COM_ACAO.has(m.proximo_passo.tipo)) || m.modulos.aprovacao?.pedido?.status === "AGUARDANDO_PAGAMENTO" ? { rotulo: "Pendente", tom: "alerta" } : null,
  };
}

/**
 * O cartao do plano atual na casca (spec 038, secao 2): na gaveta do desktop e na faixa do topo no trilho e no celular.
 * Com dois ou mais planos, o topo do cartao e o seletor; "Ver todos os planos" tira o filtro das telas de plano.
 */
export function PlanoDaCasca({ variante }: { variante: "gaveta" | "faixa" }) {
  const { planos, atual, escolher, verTodos, carregando, erro, recarregar } = usePlanoAtual();
  if (carregando) return <CartaoDoPlanoAtual estado="carregando" variante={variante} />;
  if (erro && !planos.length) return <CartaoDoPlanoAtual estado="erro" variante={variante} aoTentarDeNovo={recarregar} />;
  if (!atual) return <CartaoDoPlanoAtual estado="vazio" variante={variante} linkPlanos={marca().linkPlanos} comprarHref="/comprar" />;
  const opcoes: OpcaoDePlano[] = planos.map((m) => ({ id: m.id, nome: m.plano.nome, apoio: apoio(m), detalhe: detalhe(m), atual: m.id === atual.id, anterior: encerrado(m) }));
  const plano: PlanoNaCasca = verTodos && planos.length > 1 ? { nome: "Todos os planos", apoio: `${planos.length} planos` } : paraCasca(atual);
  return <CartaoDoPlanoAtual estado="pronto" variante={variante} plano={plano} opcoes={opcoes} verTodos={verTodos} aoEscolher={escolher} />;
}

/** Filtro das telas que sao de um plano so (as abas de Meu plano e as do Financeiro). */
export function useFiltroDePlano(): { matriculaId: string | null; nome: string | null } {
  const { atual, planos, verTodos } = usePlanoAtual();
  if (verTodos || planos.length < 2 || !atual) return { matriculaId: null, nome: null };
  return { matriculaId: atual.id, nome: atual.plano.nome };
}
